/**
 * Listado de una linea completa, con el indice de sus categorias arriba.
 *
 * El indice de categorias no es decorativo: es lo que reparte la autoridad de
 * la linea hacia cada categoria y lo que le da al comprador la forma de bajar
 * un nivel sin volver al mega menu. Debajo, los mismos filtros y el mismo
 * scroll infinito que los resultados de busqueda.
 */
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { FiltrosBusqueda } from '@elcarpintero/compartido';

import { ListadoFiltrable } from '../componentes/catalogo/ListadoFiltrable';
import { SinProductosPublicados } from '../componentes/catalogo/SinProductosPublicados';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Migas } from '../componentes/diseno/Migas';
import { useListadoInfinito } from '../hooks/use-listado-infinito';
import { useUltimoDe } from '../hooks/use-ultimo-de';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaListaProductos, esquemaMigas } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { obtenerLinea } from '../servicios/catalogo.servicio';
import {
  claveFiltros,
  conFiltros,
  escribirFiltros,
  leerFiltros,
  leerPagina,
} from '../utilidades/filtros-busqueda';
import { conteoProductos, numero } from '../utilidades/formato';
import { conPagina, rutas } from '../utilidades/rutas';

export function Linea() {
  const { slug = '' } = useParams();
  const [parametros, setParametros] = useSearchParams();
  const filtros = leerFiltros(parametros);
  const pagina = leerPagina(parametros);

  const listado = useListadoInfinito(
    JSON.stringify([slug, claveFiltros(filtros)]),
    (pedida, senal) => obtenerLinea(slug, { pagina: pedida, filtros, senal }),
    pagina,
  );
  // Al filtrar, la cabecera y la barra se quedan con lo ultimo que llego.
  const vista = useUltimoDe(slug, listado.datos);

  if (vista === null) {
    return (
      <div className="contenedor pagina">
        {listado.estado === 'cargando' ? (
          <Cargando que="la línea" pagina />
        ) : (
          <>
            <Seo
              titulo="Línea no encontrada"
              descripcion="La línea que buscas no existe en el catálogo."
              ruta={rutas.linea(slug)}
              sinIndexar
            />
            <AvisoError error={listado.error} />
          </>
        )}
      </div>
    );
  }

  const { linea, categorias } = vista;
  const migas = [
    { nombre: 'Inicio', ruta: rutas.inicio() },
    { nombre: linea.nombre, ruta: rutas.linea(linea.slug) },
  ];
  // Filtrar vuelve a la primera pagina: `escribirFiltros` no escribe la pagina.
  const filtrar = (siguientes: FiltrosBusqueda) => {
    setParametros(escribirFiltros(siguientes));
  };
  const enlacePagina = (destino: number) =>
    conPagina(conFiltros(rutas.linea(slug), filtros), destino);
  // Cada pagina es canonica de si misma (sin filtros) y se nombra distinto.
  const dePagina = vista.pagina > 1 ? ` (página ${vista.pagina})` : '';

  return (
    <>
      <Seo
        titulo={`${linea.nombre}${dePagina}`}
        descripcion={`${numero(vista.totalSinFiltros)} productos de ${linea.nombre} en El Carpintero: stock real, despacho a todo Chile y asesoría de taller.`}
        ruta={conPagina(rutas.linea(linea.slug), vista.pagina)}
      />
      <DatosEstructurados
        esquemas={[esquemaMigas(migas), esquemaListaProductos(linea.nombre, vista.productos)]}
      />

      <div className="contenedor pagina">
        <Migas migas={migas} />

        <header className="encabezado-pag">
          <div>
            <h1>{linea.nombre}</h1>
            <p className="sub">
              {numero(categorias.length)} categorias ·{' '}
              {conteoProductos(vista.total, vista.totalSinFiltros)}
            </p>
          </div>
        </header>

        {categorias.length > 0 ? (
          <nav className="tira-categorias" aria-label={`Categorías de ${linea.nombre}`}>
            <ul className="tira-marcas">
              {categorias.map((categoria) => (
                <li key={categoria.id}>
                  <Link className="marca-pil" to={rutas.categoria(categoria.slug)}>
                    {categoria.nombre}
                    <span className="cuenta">{numero(categoria.cantidadProductos)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        <ListadoFiltrable
          listado={listado}
          facetas={vista.facetas}
          filtros={filtros}
          onFiltrar={filtrar}
          titulo={`Productos de ${linea.nombre}`}
          que="los productos"
          donde={`en ${linea.nombre}`}
          vacio={<SinProductosPublicados />}
          enlacePagina={enlacePagina}
        />
      </div>
    </>
  );
}
